/* ============================================================================
   /portal/auth/reset-password - reads ?token=XXX, form for new password.
   ----------------------------------------------------------------------------
   Reskinned to match the marketing welcome panel: hero gradient + neon green
   Space Grotesk headline. Validation + token handling preserved.
   ========================================================================= */
import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";

function useHashQuery() {
  const hash = window.location.hash;
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) return new URLSearchParams();
  return new URLSearchParams(hash.slice(queryStart + 1));
}

const PAGE_BG: React.CSSProperties = {
  background: "hsl(230 35% 4%)",
};

function PageShell({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <div
      className="relative min-h-screen text-foreground flex justify-center px-4 auth-shell-safe-top pb-12 sm:items-center"
      style={PAGE_BG}
    >
      {/* Aurora glows */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: "-80px",
          right: "-60px",
          width: 360,
          height: 360,
          background: "radial-gradient(circle, hsl(142 78% 56% / 0.18), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          bottom: "-60px",
          left: "-80px",
          width: 320,
          height: 320,
          background: "radial-gradient(circle, hsl(174 60% 53% / 0.10), transparent 60%)",
        }}
      />
      <div
        className="relative w-full max-w-sm v2-neon-card p-7"
        data-testid={testId}
      >
        <div className="flex justify-center mb-5">
          <Logo size={48} />
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AuthResetPassword() {
  const { resetPassword } = useAuth();
  const params = useHashQuery();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <PageShell testId="card-reset-password-invalid">
        <div className="text-center">
          <div
            className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{
              background: "hsl(0 70% 30% / 0.35)",
              border: "1.5px solid hsl(0 70% 60% / 0.55)",
            }}
          >
            <AlertTriangle className="w-6 h-6" style={{ color: "hsl(0 80% 75%)" }} />
          </div>
          <h1
            className="v2-display text-2xl leading-tight"
            style={{ color: "var(--text-ink-bright)" }}
          >
            Invalid reset link
          </h1>
          <p
            className="text-xs mt-2 leading-relaxed"
            style={{ color: "var(--text-ink-faint)" }}
          >
            This link is missing a token. Please use the link from your email.
          </p>
          <Link
            href="/auth/forgot-password"
            className="v2-neon-cta mt-5 w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full no-underline"
            data-testid="button-request-new-reset"
          >
            Request a new link
            <ChevronRight className="w-4 h-4" />
          </Link>
          <Link
            href="/login"
            className="mt-3 block text-[11px] no-underline"
            style={{ color: "var(--text-ink-faint)" }}
            data-testid="link-back-to-portal"
          >
            Back to sign in
          </Link>
        </div>
      </PageShell>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await resetPassword(token, password);
    if (result.ok) {
      setDone(true);
    } else {
      setError(result.message ?? "Could not reset password. The link may have expired.");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <PageShell testId="card-reset-password">
        <div className="text-center">
          <div
            className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{
              background: "hsl(142 78% 28% / 0.4)",
              border: "1.5px solid hsl(142 78% 56% / 0.55)",
              boxShadow: "0 0 24px hsl(142 100% 50% / 0.3)",
            }}
          >
            <CheckCircle2 className="w-6 h-6" style={{ color: "hsl(142 78% 70%)" }} />
          </div>
          <div className="flex justify-center">
            <span className="v2-eyebrow" data-testid="badge-reset-done-eyebrow">
              <span className="v2-eyebrow__dot" />
              All set
            </span>
          </div>
          <h1
            className="v2-display mt-4 text-2xl leading-tight"
            style={{ color: "var(--text-ink-bright)" }}
          >
            Password updated.
          </h1>
          <p
            className="text-xs mt-2 leading-relaxed"
            style={{ color: "var(--text-ink-faint)" }}
          >
            Your password has been changed. You can sign in now.
          </p>
          <Link
            href="/login"
            className="v2-neon-cta mt-6 w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full no-underline"
            data-testid="button-go-signin"
          >
            Sign in
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell testId="card-reset-password">
      <div className="flex justify-center">
        <span className="v2-eyebrow" data-testid="badge-reset-eyebrow">
          <span className="v2-eyebrow__dot" />
          New password
        </span>
      </div>
      <h1
        className="v2-display mt-4 text-2xl text-center leading-tight"
        style={{ color: "var(--text-ink-bright)" }}
      >
        Choose a{" "}
        <span style={{ color: "hsl(var(--primary))" }}>new password.</span>
      </h1>
      <p
        className="text-xs text-center mt-2 mb-5 leading-relaxed"
        style={{ color: "var(--text-ink-faint)" }}
      >
        Must be at least 8 characters.
      </p>

      <form onSubmit={submit} className="space-y-3.5">
        <div>
          <Label
            htmlFor="new-password"
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: "var(--text-ink-faint)" }}
          >
            New password
          </Label>
          <Input
            id="new-password"
            data-testid="input-new-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1.5 focus-visible:ring-2"
            style={{
              background: "hsl(230 25% 9%)",
              border: "1px solid hsl(142 50% 25%)",
              color: "var(--text-ink-bright)",
            }}
          />
        </div>
        <div>
          <Label
            htmlFor="confirm-password"
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: "var(--text-ink-faint)" }}
          >
            Confirm password
          </Label>
          <Input
            id="confirm-password"
            data-testid="input-confirm-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="mt-1.5 focus-visible:ring-2"
            style={{
              background: "hsl(230 25% 9%)",
              border: "1px solid hsl(142 50% 25%)",
              color: "var(--text-ink-bright)",
            }}
          />
        </div>
        {error && (
          <p
            className="text-xs"
            style={{ color: "hsl(0 80% 70%)" }}
            data-testid="text-reset-error"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="button-reset-submit"
          className="v2-neon-cta w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full disabled:opacity-60"
        >
          {busy ? "Updating..." : "Update password"}
          {!busy && <ChevronRight className="w-4 h-4" />}
        </button>
      </form>
    </PageShell>
  );
}
